import React, { useState, useEffect, useCallback } from 'react';
import { FaTimes, FaCalculator } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { campaignService } from '../../services/companyAuthService';

const PaymentDialog = ({ isOpen, onClose, campaignId, affiliateId, affiliateData }) => {
  const [paymentData, setPaymentData] = useState({
    totalDue: 0,
    amountPaid: 0,
    amountUnpaid: 0
  });
  const [affiliateInfo, setAffiliateInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    instagramId: '',
    affiliateType: '',
    companyName: ''
  });
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [referenceMessage, setReferenceMessage] = useState('');
  const [calculating, setCalculating] = useState(false);

  useEffect(() => {
    if (affiliateData) {
      setAffiliateInfo({
        firstName: affiliateData.firstName || '',
        lastName: affiliateData.lastName || '',
        email: affiliateData.email || '',
        username: affiliateData.username || '',
        instagramId: affiliateData.instagramId || '',
        affiliateType: affiliateData.affiliateType || '',
        companyName: affiliateData.companyName || ''
      });
    }
  }, [affiliateData]);

  const fetchPaymentDetails = useCallback(async () => {
    try {
      setInitialLoading(true);
      const response = await campaignService.getAffiliatePaymentDetails(campaignId, affiliateId);
      if (response.success) {
        setPaymentData(response.data.paymentDetails);
        
        if (response.data.affiliate) {
          setAffiliateInfo({
            firstName: response.data.affiliate.firstName || '',
            lastName: response.data.affiliate.lastName || '',
            email: response.data.affiliate.email || '',
            username: response.data.affiliate.username || '',
            instagramId: response.data.affiliate.instagramId || '',
            affiliateType: response.data.affiliate.affiliateType || '',
            companyName: response.data.affiliate.companyName || ''
          });
        }
      }
    } catch (error) {
      console.error('Error fetching payment details:', error);
      // Try to use data from affiliateData prop if API fails
      if (affiliateData?.financial) {
        setPaymentData({
          totalDue: affiliateData.financial.totalDue || 0,
          amountPaid: affiliateData.financial.amountPaid || 0,
          amountUnpaid: affiliateData.financial.amountUnpaid || 0
        });
      }
    } finally {
      setInitialLoading(false);
    }
  }, [campaignId, affiliateId, affiliateData]);

  useEffect(() => {
    if (isOpen && campaignId && affiliateId) {
      fetchPaymentDetails();
    }
  }, [isOpen, campaignId, affiliateId, fetchPaymentDetails]);
  

  const handlePaymentAmountChange = (e) => {
    const value = e.target.value;
    const numValue = value === '' ? '' : parseFloat(value.replace(/^0+/, ''));
    setPaymentAmount(numValue);
  };

  const handleReferenceMessageChange = (e) => {
    setReferenceMessage(e.target.value);
  };

  const performCalculation = async (isAutoCalculation = false) => {
    try {
      const response = await campaignService.calculateAffiliatePayment(campaignId, affiliateId);
      
      if (response.success) {
        
        // Auto-fill the total due amount (includes new revenue + previous unpaid amounts)
        const calculatedAmount = response.data.total.revenue; // New revenue only
        const currentAmountPaid = paymentData.amountPaid || 0;
        
        // Use totalDue from backend which includes previous unpaid amounts
        const totalDueAmount = response.data.incrementalInfo?.totalDue || calculatedAmount;
        
        setPaymentData(prev => ({
          ...prev,
          totalDue: totalDueAmount,
          amountUnpaid: totalDueAmount - currentAmountPaid
        }));
        
        if (!isAutoCalculation) {
          if (totalDueAmount > 0) {
            const message = response.data.incrementalInfo?.isIncremental 
              ? `Payment recalculated successfully! New revenue: ₹${calculatedAmount.toFixed(2)}, Total remaining due: ₹${totalDueAmount.toFixed(2)}`
              : `Payment calculated successfully! Total due: ₹${totalDueAmount.toFixed(2)}`;
            toast.success(message);
          } else if (response.data.incrementalInfo?.isIncremental) {
            toast.info('No new revenue since last calculation. Total due remains ₹' + (currentAmountPaid || 0).toFixed(2) + ' (unpaid amount).');
          } else {
            toast.info('Payment calculated but revenue is zero. Check campaign configuration and approved media.');
          }
        }
      } else {
        if (!isAutoCalculation) {
          toast.error(response.message || 'Failed to calculate payment');
        }
        throw new Error(response.message || 'Calculation failed');
      }
    } catch (error) {
      console.error('Error calculating payment:', error);
      if (!isAutoCalculation) {
        if (error.message?.includes('No approved media')) {
          toast.warning('No approved media found for this affiliate. They need to submit and get approval for their content first.');
        } else if (error.message?.includes('Campaign not found')) {
          toast.error('Campaign not found or you do not have permission to access it.');
        } else {
          toast.error('Failed to calculate payment. Please try again.');
        }
      }
      throw error;
    }
  };

  const handleRecalculate = async () => {
    if (!campaignId || !affiliateId) {
      toast.error('Missing campaign or affiliate information');
      return;
    }
    
    setCalculating(true);
    try {
      await performCalculation();
      toast.success('Payment details recalculated successfully');
    } catch (error) {
      console.error('Recalculation failed:', error);
      toast.error('Failed to recalculate payment details');
    } finally {
      setCalculating(false);
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!paymentAmount || paymentAmount <= 0) {
      toast.error('Payment amount must be greater than 0');
      return;
    }
    
    if (paymentAmount > paymentData.amountUnpaid) {
      toast.error('Payment amount cannot exceed remaining due amount');
      return;
    }
    
    setLoading(true);
    try {
      const newAmountPaid = (paymentData.amountPaid || 0) + paymentAmount;
      const paymentPayload = {
        totalDue: paymentData.totalDue,
        amountPaid: newAmountPaid
      };
      
      // Include reference message if provided
      if (referenceMessage.trim()) {
        paymentPayload.referenceMessage = referenceMessage.trim();
      }
      
      const response = await campaignService.updateAffiliatePaymentDetails(campaignId, affiliateId, paymentPayload);

      if (response.success) {
        // Update current payment data
        setPaymentData(response.data.paymentDetails);
        
        // Reset payment amount and reference message
        setPaymentAmount('');
        setReferenceMessage('');

        toast.success('Payment processed successfully');
        
        // Close dialog after successful payment
        setTimeout(() => {
          onClose(true); // Pass true to indicate successful payment
        }, 1500);
      } else {
        toast.error(response.message || 'Failed to process payment');
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to process payment. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPaymentAmount('');
    setReferenceMessage('');
    onClose(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
          aria-hidden="true"
          onClick={handleClose}
        ></div>

        {/* Center modal */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-4 py-4 sm:px-6 flex justify-between items-center">
            <h3 className="text-lg sm:text-xl font-semibold text-white" id="modal-title">
              Process Payment
            </h3>
            <button
              onClick={handleClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <FaTimes size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 max-h-[70vh] overflow-y-auto">
            {initialLoading ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Affiliate Information Section */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h4 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">Affiliate Information</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div>
                        <p className="text-xs sm:text-sm text-gray-500 mb-1">Name</p>
                        <p className="text-sm sm:text-base font-medium text-gray-900">
                          {affiliateInfo.firstName && affiliateInfo.lastName 
                            ? `${affiliateInfo.firstName} ${affiliateInfo.lastName}`
                            : affiliateInfo.username || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm text-gray-500 mb-1">Email</p>
                        <p className="text-sm sm:text-base font-medium text-gray-900">{affiliateInfo.email || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <p className="text-xs sm:text-sm text-gray-500 mb-1">Instagram ID</p>
                        <p className="text-sm sm:text-base font-medium text-gray-900">{affiliateInfo.instagramId || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm text-gray-500 mb-1">Affiliate Type</p>
                        <p className="text-sm sm:text-base font-medium text-gray-900 capitalize">{affiliateInfo.affiliateType || 'N/A'}</p>
                      </div>
                      {affiliateInfo.companyName && (
                        <div>
                          <p className="text-xs sm:text-sm text-gray-500 mb-1">Company Name</p>
                          <p className="text-sm sm:text-base font-medium text-gray-900">{affiliateInfo.companyName}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Payment Summary Section */}
                <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                  <h4 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">Payment Details</h4>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2">
                      <span className="text-sm sm:text-base text-gray-700">Total Amount:</span>
                      <span className="text-sm sm:text-base font-semibold text-gray-900">₹{(paymentData.totalDue || 0).toFixed(2)}</span>
                    </div>
                    
                    <div className="flex justify-between items-center py-2">
                      <span className="text-sm sm:text-base text-gray-700">Paid Amount:</span>
                      <span className="text-sm sm:text-base font-semibold text-green-600">₹{(paymentData.amountPaid || 0).toFixed(2)}</span>
                    </div>
                    
                    <div className="flex justify-between items-center py-2 border-t border-orange-300 pt-3">
                      <span className="text-sm sm:text-base font-semibold text-gray-800">Due Amount:</span>
                      <span className="text-lg sm:text-xl font-bold text-orange-600">₹{(paymentData.amountUnpaid || 0).toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Payment Form Section */}
                {paymentData.amountUnpaid > 0 && (
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h4 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">Make Payment</h4>
                    
                    <form onSubmit={handlePaymentSubmit} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Payment Amount
                        </label>
                        <input
                          type="number"
                          value={paymentAmount}
                          onChange={handlePaymentAmountChange}
                          placeholder="Enter payment amount"
                          className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                          min="0"
                          step="0.01"
                          max={paymentData.amountUnpaid || 0}
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Maximum: ₹{(paymentData.amountUnpaid || 0).toFixed(2)}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Reference Message (Optional)
                        </label>
                        <textarea
                          value={referenceMessage}
                          onChange={handleReferenceMessageChange}
                          placeholder="Enter reference message or transaction details (e.g., Transaction ID, UPI Ref No, Bank Transfer ID)"
                          className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors resize-none"
                          rows="3"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Add a note or reference for this payment record
                        </p>
                      </div>
                      
                      <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 pt-2">
                        <button
                          type="button"
                          onClick={handleRecalculate}
                          disabled={calculating}
                          className="flex items-center justify-center px-4 py-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-base transition-colors"
                        >
                          <FaCalculator className="mr-2" />
                          {calculating ? 'Recalculating...' : 'Re-calculate Payment'}
                        </button>
                        <div className="flex space-x-3">
                          <button
                            type="button"
                            onClick={handleClose}
                            className="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium text-base transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            disabled={loading || !paymentAmount || paymentAmount <= 0}
                            className="px-6 py-2.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-base transition-colors"
                          >
                            {loading ? 'Processing...' : 'Record Pay'}
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                )}

                {paymentData.amountUnpaid === 0 && (
                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <div className="flex items-center justify-center text-green-700">
                      <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="font-semibold">Payment fully settled!</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentDialog;

